import { useState, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { spaceSharingService } from "@/services/spaceSharingService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Loader2, 
  ArrowRight,
  Share2,
  Lock
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { buildLoginUrlWithReturnTo } from "@/lib/publicRoutes";

export default function SpaceShareAcceptance() {
  const [, params] = useRoute("/spaces/share/:token");
  const [location, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const token = params?.token || "";

  const [hasResponded, setHasResponded] = useState(false);

  // Validate share link
  const { data: validation, isLoading: isValidating, refetch: refetchValidation } = useQuery({
    queryKey: ["/api/spaces/share/validate", token],
    queryFn: async () => {
      if (!token) return null;
      return await spaceSharingService.validateShareLink(token);
    },
    enabled: !!token,
    retry: false,
  });

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      return await spaceSharingService.acceptShareInvitation(token);
    },
    onSuccess: () => {
      setHasResponded(true);
      toast({
        title: "Access granted",
        description: "You now have access to this space",
      });
      // Redirect to space after a short delay
      setTimeout(() => {
        if (validation?.space) {
          setLocation(`/spaces/${validation.space.id}`);
        }
      }, 1500);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to accept",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  // Reject invitation mutation
  const rejectMutation = useMutation({
    mutationFn: async () => {
      return await spaceSharingService.rejectShareInvitation(token);
    },
    onSuccess: () => {
      setHasResponded(true);
      toast({
        title: "Invitation declined",
        description: "You have declined access to this space",
      });
      // Redirect to spaces list after a short delay
      setTimeout(() => {
        setLocation("/spaces");
      }, 1500);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to reject",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  // Handle accept
  const handleAccept = () => {
    acceptMutation.mutate();
  };

  // Handle reject
  const handleReject = () => {
    rejectMutation.mutate();
  };

  // Loading state
  if (authLoading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Validating share link...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Sign in required
            </CardTitle>
            <CardDescription>
              You need to sign in to access this shared space
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Authentication required</AlertTitle>
              <AlertDescription>
                Please sign in or create an account to accept this space invitation.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => setLocation(buildLoginUrlWithReturnTo(location))}
              className="w-full"
              data-testid="button-go-to-login"
            >
              Go to Sign In
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid link
  if (!validation || !validation.isValid) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Invalid Link
            </CardTitle>
            <CardDescription>
              This share link is not valid or has been deactivated
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Link not valid</AlertTitle>
              <AlertDescription>
                {validation?.message || "The share link you're trying to access is invalid, expired, or has been revoked."}
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => setLocation("/spaces")}
              variant="outline"
              className="w-full"
              data-testid="button-go-to-spaces"
            >
              Go to Spaces
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { share, space, access } = validation;

  // Check if user is the owner
  const isOwner = space && user.id === share?.createdByUserId;

  // Already accepted
  if (access?.status === "accepted" || hasResponded) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Access Granted
            </CardTitle>
            <CardDescription>
              You already have access to this space
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>You have access</AlertTitle>
              <AlertDescription>
                {isOwner
                  ? "This is your own space. Redirecting..."
                  : "You have already accepted access to this space. Redirecting..."}
              </AlertDescription>
            </Alert>
            {space && (
              <Button
                onClick={() => setLocation(`/spaces/${space.id}`)}
                className="w-full"
                data-testid="button-go-to-space"
              >
                Go to Space
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already rejected
  if (access?.status === "rejected") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Access Declined
            </CardTitle>
            <CardDescription>
              You have previously declined access to this space
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Access declined</AlertTitle>
              <AlertDescription>
                You previously declined the invitation to access this space.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => setLocation("/spaces")}
              variant="outline"
              className="w-full"
              data-testid="button-go-to-spaces-rejected"
            >
              Go to Spaces
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show accept/reject prompt
  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Space Invitation
          </CardTitle>
          <CardDescription>
            You've been invited to access a shared space
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {space && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <div
                  className="p-2 rounded-md flex-shrink-0"
                  style={{ backgroundColor: `${space.color || "hsl(220, 70%, 50%)"}15` }}
                >
                  <span className="text-lg">{space.icon || "🧠"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{space.name}</h3>
                  {space.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                      {space.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Accept invitation?</AlertTitle>
            <AlertDescription>
              {share?.accessType === "restricted"
                ? "Accepting will grant you access to view and collaborate in this space."
                : "This space is shared publicly. Accepting will grant you access."}
            </AlertDescription>
          </Alert>

          {share?.expiresAt && (
            <p className="text-xs text-muted-foreground text-center">
              Link expires {formatDistanceToNow(new Date(share.expiresAt), { addSuffix: true })}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={acceptMutation.isPending || rejectMutation.isPending || hasResponded}
              className="flex-1"
              data-testid="button-reject-invitation"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={acceptMutation.isPending || rejectMutation.isPending || hasResponded}
              className="flex-1"
              data-testid="button-accept-invitation"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}








